// WS-8 P1 — Importar programación por OP desde Excel (formato del
// "Seguimiento semanal" de Alpina: Fecha · Orden · SKU · Cantidad · Obs).
//
// Tolerancias del Excel real (validadas contra SOPO/):
//   - Orden '-' o '—' o vacía → numero_op NULL (fila programada sin OP; igual
//     suma al nivel de servicio).
//   - Fechas sucias ('23//05/2026', seriales, d/m/y) → parseFechaISO.
//   - Área opcional: si no viene, se deriva del SKU (skus_lineas → área);
//     ambigua/sin config → picker por fila (mismo criterio que C4).
//
// Parseo + mapeo en el FE (SheetJS) → POST /facturacion/programacion-op/importar
// (upsert por cliente+planta+OP+SKU+fecha: recargar actualiza, no duplica).

import { useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { ZodError } from 'zod';
import { toast } from 'sonner';
import type { ProgramacionOpList } from 'schemas';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { useImportarProgramacionOp } from '../../lib/api/queries';
import { ApiError } from '../../lib/api/client';
import { cn } from '../../lib/utils';
import { esFechaISOValida, parseFechaISO } from './excelFecha';

type Data = ProgramacionOpList;

const OP_REGEX = /^\d{8}$/;
/** Celda de Orden sin OP en el Excel real: '-', '—', 'N/A', vacío. */
const SIN_OP = new Set(['', '-', '—', 'n/a', 'na', 'sin op']);
/** Etiquetas humanas para los campos del Zod compartido (toast de error). */
const CAMPO_LABEL: Record<string, string> = {
  fecha_programada: 'Fecha',
  numero_op: 'OP',
  sku_id: 'SKU',
  area_id: 'Área',
  cantidad_programada: 'Cantidad',
  observacion: 'Observación',
};

interface FilaParseada {
  idx: number;
  filaExcel: number;
  fecha: string;
  numeroOp: string | null;
  skuCodigo: string;
  cantidad: number | null;
  observacion: string | null;
  sku_id: string | null;
  candidatas: string[];
  areaExcel: string | null;
  areaTexto: string;
  autoArea: string | null;
  errorBase: string | null;
}

export function FactImportarProgramacionOpDialog({
  data,
  onClose,
}: {
  data: Data;
  onClose: () => void;
}) {
  const clientesOrdenados = useMemo(
    () => [...data.clientes].sort((a, b) => Number(b.es_principal) - Number(a.es_principal)),
    [data.clientes],
  );
  const [clienteId, setClienteId] = useState(clientesOrdenados[0]?.id ?? '');
  const [filas, setFilas] = useState<FilaParseada[]>([]);
  const [picks, setPicks] = useState<Record<number, string>>({});
  const [nombreArchivo, setNombreArchivo] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const importar = useImportarProgramacionOp();

  const norm = (s: string) => s.trim().toLowerCase();
  const skusDelCliente = useMemo(
    () => data.skus.filter((s) => s.cliente_id === clienteId),
    [data.skus, clienteId],
  );
  const skuPorCodigo = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of skusDelCliente) m.set(norm(s.codigo_cliente), s.id);
    return m;
  }, [skusDelCliente]);
  const skuDesc = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of skusDelCliente) m.set(norm(s.codigo_cliente), s.descripcion);
    return m;
  }, [skusDelCliente]);
  const areasDeSku = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const sa of data.skus_areas) {
      const arr = m.get(sa.sku_id) ?? [];
      if (!arr.includes(sa.area_id)) arr.push(sa.area_id);
      m.set(sa.sku_id, arr);
    }
    return m;
  }, [data.skus_areas]);
  const areaLabel = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of data.areas) m.set(a.id, `${a.codigo} · ${a.nombre}`);
    return m;
  }, [data.areas]);
  // Texto del Excel → area_id. Reconoce nombre/código de ÁREA y también de
  // LÍNEA (ej. "FLOWPACK" → el área de esa línea). Colisiones del mismo texto
  // entre áreas distintas → null (ambiguo → picker); los textos de área tienen
  // prioridad sobre los de línea.
  const areaPorTexto = useMemo(() => {
    const m = new Map<string, string | null>();
    const add = (key: string, id: string) => {
      if (!key) return;
      if (m.has(key) && m.get(key) !== id) m.set(key, null);
      else m.set(key, id);
    };
    for (const a of data.areas) {
      add(norm(a.nombre), a.id);
      add(norm(a.codigo), a.id);
    }
    const clavesDeArea = new Set(m.keys());
    for (const l of data.lineas) {
      if (!clavesDeArea.has(norm(l.nombre))) add(norm(l.nombre), l.area_id);
      if (!clavesDeArea.has(norm(l.codigo))) add(norm(l.codigo), l.area_id);
    }
    return m;
  }, [data.areas, data.lineas]);

  // Lookup tolerante a singular/plural: "ATLANTA" matchea el área "Atlantas".
  const resolverAreaTexto = (texto: string): string | null => {
    const t = norm(texto);
    if (!t) return null;
    const directo = areaPorTexto.get(t);
    if (directo !== undefined) return directo;
    const plural = areaPorTexto.get(`${t}s`);
    if (plural !== undefined) return plural;
    const singular = t.endsWith('s') ? areaPorTexto.get(t.slice(0, -1)) : undefined;
    return singular ?? null;
  };

  function descargarPlantilla() {
    const cliente = data.clientes.find((c) => c.id === clienteId);
    const wb = XLSX.utils.book_new();
    const header = [
      'Fecha (YYYY-MM-DD)',
      'Orden (8 dígitos o "-")',
      'SKU (codigo cliente)',
      'Cantidad programada',
      'Área (opcional)',
      'Observaciones (opcional)',
    ];
    const ejemplo = [
      new Date().toISOString().slice(0, 10),
      '10349726',
      skusDelCliente[0]?.codigo_cliente ?? '11656',
      14805,
      data.areas[0]?.nombre ?? 'Verticales',
      'ejemplo — borrar',
    ];
    const ws = XLSX.utils.aoa_to_sheet([header, ejemplo]);
    XLSX.utils.book_append_sheet(wb, ws, 'ProgramacionOP');
    const refSkus = [
      ['SKUS VÁLIDOS DE ' + (cliente?.nombre ?? ''), ''],
      ['Código', 'Descripción'],
      ...skusDelCliente.map((s) => [s.codigo_cliente, s.descripcion]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(refSkus), 'Referencia');
    const refAreas = [
      ['ÁREAS VÁLIDAS — nombre o código en la columna "Área (opcional)"'],
      ['Código', 'Área', 'Planta'],
      ...data.areas.map((a) => [a.codigo, a.nombre, a.planta_nombre]),
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(refAreas), 'Áreas');
    XLSX.writeFile(wb, `programacion-op-${cliente?.nombre ?? 'cliente'}.xlsx`);
  }

  function onArchivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setNombreArchivo(file.name);
    setPicks({});
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target?.result, { type: 'array' });
        const ws = wb.Sheets['ProgramacionOP'] ?? wb.Sheets[wb.SheetNames[0]!];
        const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws!, { header: 1, raw: false, defval: '' });
        const aoaRaw = XLSX.utils.sheet_to_json<unknown[]>(ws!, {
          header: 1,
          raw: true,
          defval: '',
        });
        const out: FilaParseada[] = [];
        for (let i = 1; i < aoa.length; i++) {
          const r = aoa[i]!;
          // Columnas: Fecha(0) · Orden(1) · SKU(2) · Cantidad(3) · Área opc(4) · Obs(5)
          const fecha = parseFechaISO(aoaRaw[i]?.[0] ?? r[0]);
          const opRaw = String(r[1] ?? '').trim();
          const skuCodigo = String(r[2] ?? '').trim();
          const cantRaw = String(r[3] ?? '').trim();
          const areaTexto = String(r[4] ?? '').trim();
          const obsRaw = String(r[5] ?? '').trim();
          if (!fecha && !opRaw && !skuCodigo && !cantRaw) continue;
          const numeroOp = SIN_OP.has(norm(opRaw)) ? null : opRaw;
          const sku_id = skuPorCodigo.get(norm(skuCodigo)) ?? null;
          const candidatas = sku_id ? (areasDeSku.get(sku_id) ?? []) : [];
          const areaExcel = areaTexto ? resolverAreaTexto(areaTexto) : null;
          const cantidad =
            cantRaw === '' ? null : Number(cantRaw.replace(/\./g, '').replace(',', '.'));
          // La cantidad NO va a errorBase: es editable en el preview (la fila
          // queda marcada hasta corregirla o quitarla).
          let errorBase: string | null = null;
          if (!esFechaISOValida(fecha))
            errorBase = `Fecha "${fecha}" inválida (use AAAA-MM-DD, año-mes-día)`;
          else if (numeroOp !== null && !OP_REGEX.test(numeroOp))
            errorBase = `Orden "${numeroOp}" no es de 8 dígitos (use "-" si no hay OP)`;
          else if (!sku_id) errorBase = `SKU "${skuCodigo}" no es de este cliente`;
          out.push({
            idx: out.length,
            filaExcel: i + 1,
            fecha,
            numeroOp,
            skuCodigo,
            cantidad,
            observacion: obsRaw === '' ? null : obsRaw,
            sku_id,
            candidatas,
            areaExcel,
            areaTexto,
            autoArea: candidatas.length === 1 ? candidatas[0]! : null,
            errorBase,
          });
        }
        setFilas(out);
        if (out.length === 0) toast.error('El archivo no tiene filas de datos');
      } catch {
        toast.error('No se pudo leer el archivo. ¿Es un .xlsx válido?');
      }
    };
    reader.readAsArrayBuffer(file);
  }

  const areaEfectiva = (f: FilaParseada): string | null =>
    f.areaExcel ?? f.autoArea ?? picks[f.idx] ?? null;
  const cantidadValida = (f: FilaParseada): boolean =>
    f.cantidad !== null && Number.isFinite(f.cantidad) && f.cantidad >= 0;
  const sinArea = filas.filter((f) => !f.errorBase && areaEfectiva(f) === null);
  const cantidadesInvalidas = filas.filter((f) => !f.errorBase && !cantidadValida(f));
  const errores = filas.filter((f) => f.errorBase);
  const cargables = filas.filter(
    (f) => !f.errorBase && areaEfectiva(f) !== null && cantidadValida(f),
  );
  const puedeCargar =
    errores.length === 0 &&
    sinArea.length === 0 &&
    cantidadesInvalidas.length === 0 &&
    cargables.length > 0;

  // Edición en el preview: la precarga se puede ajustar antes de confirmar.
  const quitarFila = (idx: number) => setFilas((prev) => prev.filter((f) => f.idx !== idx));
  const cambiarCantidad = (idx: number, valor: string) =>
    setFilas((prev) =>
      prev.map((f) =>
        f.idx === idx ? { ...f, cantidad: valor === '' ? null : Number(valor) } : f,
      ),
    );

  async function cargar() {
    if (!puedeCargar) return;
    try {
      const res = await importar.mutateAsync({
        cliente_id: clienteId,
        filas: cargables.map((f) => ({
          area_id: areaEfectiva(f)!,
          fecha_programada: f.fecha,
          numero_op: f.numeroOp,
          sku_id: f.sku_id!,
          cantidad_programada: f.cantidad!,
          observacion: f.observacion,
        })),
      });
      toast.success(
        `Programación cargada: ${res.insertadas} nueva(s), ${res.actualizadas} actualizada(s)`,
      );
      onClose();
    } catch (err) {
      // El Zod compartido corre en el mutationFn ANTES del POST: si tira acá,
      // el preview dejó pasar algo que el contrato rechaza — señalar fila y
      // campo en vez del genérico (incidente Sopó 2026-07-03: fechas Y-DD-MM).
      let msg = 'Error al cargar';
      if (err instanceof ApiError) {
        msg = err.message;
      } else if (err instanceof ZodError && err.issues[0]) {
        const issue = err.issues[0];
        const idx = issue.path[1];
        const fila = typeof idx === 'number' ? cargables[idx] : undefined;
        const campo = CAMPO_LABEL[String(issue.path[2] ?? '')] ?? 'Datos';
        msg = `${fila ? `Fila ${fila.filaExcel} del Excel — ` : ''}${campo}: ${issue.message}`;
        console.error('[import-op] payload inválido', err.flatten());
      } else {
        console.error('[import-op]', err);
      }
      toast.error('No se pudo cargar', { description: msg });
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-4xl">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Importar programación por OP desde Excel</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="imp-op-cliente">Cliente</Label>
              <Select
                id="imp-op-cliente"
                value={clienteId}
                onChange={(e) => {
                  setClienteId(e.target.value);
                  setFilas([]);
                  setPicks({});
                }}
              >
                {clientesOrdenados.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nombre}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="outline" type="button" onClick={descargarPlantilla}>
              Descargar plantilla
            </Button>
            <Button variant="outline" type="button" onClick={() => inputRef.current?.click()}>
              Subir archivo
            </Button>
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={onArchivo}
            />
            {nombreArchivo ? (
              <span className="text-xs text-fg-tertiary">{nombreArchivo}</span>
            ) : null}
          </div>

          {filas.length > 0 ? (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                {[
                  { label: 'Listas', value: cargables.length.toLocaleString('es-CO') },
                  {
                    label: 'Con OP',
                    value: cargables
                      .filter((f) => f.numeroOp !== null)
                      .length.toLocaleString('es-CO'),
                  },
                  {
                    label: 'Sin OP',
                    value: cargables
                      .filter((f) => f.numeroOp === null)
                      .length.toLocaleString('es-CO'),
                  },
                  {
                    label: 'Unidades',
                    value: cargables
                      .reduce((a, f) => a + (f.cantidad ?? 0), 0)
                      .toLocaleString('es-CO'),
                  },
                ].map((k) => (
                  <div
                    key={k.label}
                    className="rounded border border-border bg-surface-overlay/40 px-3 py-2"
                  >
                    <div className="text-[10px] uppercase tracking-wider text-fg-tertiary">
                      {k.label}
                    </div>
                    <div className="mt-0.5 text-base font-semibold tabular-nums text-fg">
                      {k.value}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-sm">
                {cargables.length} lista(s)
                {sinArea.length > 0 ? (
                  <span className="text-warn"> · {sinArea.length} sin área (elija abajo)</span>
                ) : null}
                {cantidadesInvalidas.length > 0 ? (
                  <span className="text-warn">
                    {' '}
                    · {cantidadesInvalidas.length} sin cantidad (corríjala o quite la fila)
                  </span>
                ) : null}
                {errores.length > 0 ? (
                  <span className="text-crit"> · {errores.length} con error</span>
                ) : null}
              </p>

              <div className="max-h-80 overflow-auto rounded border border-border">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 z-10 bg-surface-overlay text-fg-tertiary">
                    <tr>
                      <th className="px-2 py-1 text-left">Fila</th>
                      <th className="px-2 py-1 text-left">Fecha</th>
                      <th className="px-2 py-1 text-left">OP</th>
                      <th className="px-2 py-1 text-left">SKU</th>
                      <th className="px-2 py-1 text-right">Cant.</th>
                      <th className="min-w-48 px-2 py-1 text-left">Área</th>
                      <th className="px-2 py-1" aria-label="Acciones" />
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f) => (
                      <tr key={f.idx} className="border-t border-border">
                        <td className="px-2 py-2 align-middle text-fg-tertiary">{f.filaExcel}</td>
                        <td className="px-2 py-2 align-middle tabular-nums">{f.fecha || '—'}</td>
                        <td className="px-2 py-2 align-middle tabular-nums">
                          {f.numeroOp ?? <span className="text-fg-tertiary">sin OP</span>}
                        </td>
                        <td className="px-2 py-2 align-middle">
                          <div className="font-medium">{f.skuCodigo}</div>
                          {skuDesc.get(norm(f.skuCodigo)) ? (
                            <div className="text-fg-tertiary">{skuDesc.get(norm(f.skuCodigo))}</div>
                          ) : null}
                        </td>
                        <td className="px-2 py-2 text-right align-middle">
                          {f.errorBase ? (
                            <span className="tabular-nums">
                              {f.cantidad?.toLocaleString('es-CO') ?? '—'}
                            </span>
                          ) : (
                            <Input
                              type="number"
                              min={0}
                              value={f.cantidad ?? ''}
                              onChange={(e) => cambiarCantidad(f.idx, e.target.value)}
                              className={cn(
                                'h-8 w-24 text-right text-xs tabular-nums',
                                !cantidadValida(f) && 'border-crit',
                              )}
                              aria-label={`Cantidad fila ${f.filaExcel}`}
                            />
                          )}
                        </td>
                        <td className="min-w-48 px-2 py-2 align-middle">
                          {f.errorBase ? (
                            <span className="text-crit">{f.errorBase}</span>
                          ) : f.areaExcel ? (
                            <span className="text-ok">
                              {areaLabel.get(f.areaExcel)}{' '}
                              <span className="text-fg-tertiary">· del Excel</span>
                            </span>
                          ) : f.autoArea ? (
                            <span className="text-ok">{areaLabel.get(f.autoArea)}</span>
                          ) : (
                            <div className="flex flex-col gap-1">
                              {f.areaTexto ? (
                                <span className="text-warn">“{f.areaTexto}” no reconocida</span>
                              ) : null}
                              <Select
                                value={picks[f.idx] ?? ''}
                                onChange={(e) =>
                                  setPicks((p) => ({ ...p, [f.idx]: e.target.value }))
                                }
                                className="h-8 w-full min-w-44 text-xs"
                              >
                                <option value="">
                                  {f.candidatas.length === 0
                                    ? 'Elija un área (SKU sin área configurada)'
                                    : 'Elija un área'}
                                </option>
                                {(f.candidatas.length > 0
                                  ? f.candidatas
                                  : data.areas.map((a) => a.id)
                                ).map((aid) => (
                                  <option key={aid} value={aid}>
                                    {areaLabel.get(aid)}
                                  </option>
                                ))}
                              </Select>
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right align-middle">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => quitarFila(f.idx)}
                            title="Quitar esta fila de la carga"
                            aria-label={`Quitar fila ${f.filaExcel}`}
                          >
                            ✕
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <p className="text-sm text-fg-tertiary">
              Elija el cliente, descargue la plantilla (Fecha · Orden · SKU · Cantidad), complétela
              y súbala. La Orden es de 8 dígitos; si Alpina no asignó OP, deje “-” (la fila igual
              cuenta para el nivel de servicio). Recargar el mismo archivo actualiza cantidades, no
              duplica. También puede cargar OP atrasadas de días anteriores.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={cargar} disabled={!puedeCargar || importar.isPending}>
            {importar.isPending ? 'Cargando…' : `Cargar ${cargables.length} fila(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
